import { imageApi } from '@/services/imageApi';
import { loggingUtility } from '@/utils/loggingUtility';
import { useEffect, useState } from 'react';

/**
 * Checks if a string is a temp key (starts with "temp/")
 */
const isTempKey = (value: string | null): boolean => {
  return value !== null && value.startsWith('temp/');
};

/**
 * Checks if a string is a full URL (starts with http:// or https://)
 */
const isUrl = (value: string | null): boolean => {
  return (
    value !== null &&
    (value.startsWith('http://') || value.startsWith('https://'))
  );
};

/**
 * Hook to resolve image URL from tempKey or permanent CloudFront URL
 * - If temp key (starts with "temp/"): requests presigned GET URL from backend
 * - If CloudFront URL (starts with "http://" or "https://"): returns as-is (public URL)
 * - Otherwise: returns null (invalid format)
 */
export const useImageUrl = (
  imageKey: string | null
): { imageUrl: string | null; isLoading: boolean; error: Error | null } => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset state when imageKey changes
    setImageUrl(null);
    setError(null);
    setIsLoading(false);

    if (!imageKey) {
      return;
    }

    // If it's a full URL (permanent CloudFront/public image), use it directly
    if (isUrl(imageKey)) {
      setImageUrl(imageKey);
      return;
    }

    // If it's a temp key, request presigned GET URL
    if (isTempKey(imageKey)) {
      setIsLoading(true);
      imageApi
        .requestTempGetUrl({ tempKey: imageKey })
        .then(response => {
          setImageUrl(response.getUrl);
          setIsLoading(false);
        })
        .catch(err => {
          loggingUtility.error('Failed to get temp image URL', err);
          setError(
            err instanceof Error ? err : new Error('Failed to load image')
          );
          setIsLoading(false);
        });
      return;
    }

    // Invalid format - neither temp key nor URL
    loggingUtility.warn('Invalid image key format (expected temp key or URL)', {
      imageKey,
    });
    setError(new Error('Invalid image key format'));
  }, [imageKey]);

  return { imageUrl, isLoading, error };
};
